import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useEstateContext } from '@/lib/EstateContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, Loader2, Sparkles, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { createPageUrl } from '@/utils';
import { supabase } from '@/lib/supabase';

export default function UpgradeSuccess() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeEstateId } = useEstateContext();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const verifySubscription = async () => {
      // Step 1: Ask Paystack directly whether the transaction succeeded or failed.
      // This catches hard-declined cards before we waste time polling.
      const urlParams = new URLSearchParams(window.location.search);
      const reference = urlParams.get('reference') || urlParams.get('trxref');

      if (reference) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const verifyRes = await fetch('/api/paystack-manage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ action: 'verify_transaction', reference }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.transactionStatus === 'failed' || verifyData.transactionStatus === 'abandoned') {
            if (!cancelled) { setFailed(true); setVerifying(false); }
            return;
          }
        } catch (_) {
          // If verify call fails, fall through to polling — don't block the user
        }
      }

      // Step 2: Poll for activation — Paystack webhooks typically fire within 2-5 seconds
      const maxAttempts = 8;
      const pollInterval = 2500;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) return;
        // Short initial delay, then poll every 2.5s
        await new Promise(resolve => setTimeout(resolve, attempt === 1 ? 2000 : pollInterval));

        const { data: estate } = await supabase
          .from('estates')
          .select('subscription_status, subscription_tier')
          .eq('id', activeEstateId)
          .single();

        if (estate?.subscription_status === 'active') {
          if (!cancelled) { setSuccess(true); setVerifying(false); }
          return;
        }
      }

      // Webhook hasn't arrived yet — payment was received but activation is still pending
      if (!cancelled) { setSuccess(false); setVerifying(false); }
    };

    if (activeEstateId) {
      verifySubscription();
    } else {
      if (!cancelled) setVerifying(false);
    }

    return () => { cancelled = true; };
  }, [activeEstateId]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Card className="p-8 text-center max-w-md">
          <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: '#1883FF' }} />
          <h2 className="text-2xl font-bold mb-2 text-slate-900 dark:text-slate-100">Activating Subscription...</h2>
          <p className="text-slate-600 dark:text-slate-400">Your payment went through — confirming activation now</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-12 text-center max-w-lg shadow-2xl">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            <div className={`w-24 h-24 ${
              failed ? 'bg-red-100 dark:bg-red-900/30'
              : success ? 'bg-green-100 dark:bg-green-900/30'
              : 'bg-amber-100 dark:bg-amber-900/30'
            } rounded-full flex items-center justify-center mx-auto mb-6`}>
              {failed
                ? <XCircle className="w-16 h-16 text-red-600 dark:text-red-400" />
                : success
                ? <CheckCircle2 className="w-16 h-16 text-green-600 dark:text-green-400" />
                : <Clock className="w-16 h-16 text-amber-600 dark:text-amber-400" />}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              {failed ? 'Payment Unsuccessful' : success ? 'Subscription Activated! 🎉' : 'Payment Received'}
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-2">
              {failed ? 'Your payment could not be processed.' : 'Thank you for subscribing to FixMyHood Estate Management'}
            </p>
            <p className="text-slate-500 dark:text-slate-400 mb-8">
              {failed
                ? 'No charge was made to your account. This could be due to an expired card, insufficient funds, or a bank security check.'
                : success
                ? 'Your subscription is now active. You have full access to all features!'
                : 'Your subscription is being activated. This may take a few moments.'}
            </p>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-slate-800 dark:to-slate-700 rounded-xl p-6 mb-8 border border-green-200 dark:border-slate-600">
              <div className="flex items-start gap-3 text-left">
                <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-300 mb-2">What's Next?</h3>
                  <ul className="space-y-1 text-sm text-green-800 dark:text-green-200">
                    <li>✅ Full access to create and manage internal issues</li>
                    <li>✅ Unlimited contractor assignments</li>
                    <li>✅ Monthly reports and analytics</li>
                    <li>✅ Priority email support</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {failed ? (
                <>
                  <Button
                    onClick={() => navigate(createPageUrl('Profile') + '?tab=subscription')}
                    className="w-full bg-[#1883FF] hover:bg-[#0066CC] text-white text-lg py-6"
                    size="lg"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => navigate(createPageUrl('EstateDashboard'))}
                    variant="outline"
                    className="w-full"
                  >
                    Back to Dashboard
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => navigate(createPageUrl('EstateDashboard'))}
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6"
                    size="lg"
                  >
                    Go to Dashboard
                  </Button>
                  {success ? (
                    <Button
                      onClick={() => navigate(createPageUrl('ManageSubscription'))}
                      variant="outline"
                      className="w-full"
                    >
                      View Billing Settings
                    </Button>
                  ) : (
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                      className="w-full"
                    >
                      Check Activation Status
                    </Button>
                  )}
                </>
              )}
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 mt-6">
              {failed
                ? 'Need help? Contact us at support@fixmyhood.app'
                : success
                ? 'A confirmation email has been sent to your inbox'
                : 'Still activating — you can go to the dashboard now or check back in a moment. You will not be charged twice.'}
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
}
